import React, { useMemo, useEffect, useRef } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import he from "he";
import DOMPurify from "dompurify";
import Input from "./Common/Input";
import ReactSelect from "react-select";

const RenderLatexContent = ({
  content,
  data,
  userAnswer = [],
  updateUserAnswer,
  isAttempted,
  isCorrectAnswert,
  isTestAttempt,
}) => {
  const containerRef = useRef(null);

  const segments = useMemo(() => {
    if (!content) return [];

    // 1. Decode HTML entities (loop to handle double/triple encoding)
    let html = content;
    let prev;
    do {
      prev = html;
      html = he.decode(html);
    } while (html !== prev);
    html = html.trim();
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    if (
      tempDiv.children.length === 1 &&
      tempDiv.firstElementChild.tagName.toLowerCase() === "p"
    ) {
      html = tempDiv.firstElementChild.innerHTML;
    }

    // Remove any <span class="katex"><span class="katex-mathml">...</span></span> wrappers and replace with the inner content (including <br> or any HTML)
    html = html.replace(
      /<span class="katex"><span class="katex-mathml">([\s\S]*?)<\/span><\/span>/g,
      (match, inner) => inner
    );

    // 2. Process custom LaTeX markers (@_m_@)
    let processedHtml = html.replace(
      /@_m_@([\s\S]*?)@_m_@/g,
      (_, tex) =>
        `<span class="latex-inline" data-latex="${encodeURIComponent(
          tex
        )}"></span>`
    );

    // Handle cases where @_m_@ might be at the end without a closing tag
    processedHtml = processedHtml.replace(
      /@_m_@([\s\S]*?)(?=<|$)/g,
      (_, tex) =>
        `<span class="latex-inline" data-latex="${encodeURIComponent(
          tex
        )}"></span>`
    );

    // 3. Sanitize HTML to prevent XSS, allowing necessary tags and attributes
    const cleanHtml = DOMPurify.sanitize(processedHtml, {
      ADD_TAGS: ["span", "br", "img"],
      ADD_ATTR: [
        "data-latex",
        "src",
        "alt",
        "class",
        "title",
        "style",
        "data-equation-content",
      ],
    });

    // 4. Replace <img.equation_image> with <span.latex-inline> for KaTeX
    const parser = document.createElement("div");
    parser.innerHTML = cleanHtml;
    parser.querySelectorAll("img.equation_image").forEach((img) => {
      // Prioritize data-equation-content for the most accurate LaTeX string
      const rawTex =
        decodeURIComponent(img.getAttribute("data-equation-content") || "") ||
        img.getAttribute("alt") ||
        ""; // Fallback to alt or empty string

      if (rawTex) {
        const span = document.createElement("span");
        span.className = "latex-inline";
        span.setAttribute("data-latex", encodeURIComponent(rawTex));
        img.replaceWith(span);
      } else {
        console.warn(
          "No LaTeX content found for equation_image, removing img:",
          img
        );
        img.remove(); // Or decide to keep the original img if TeX isn't found
      }
    });

    const finalHtml = parser.innerHTML;

    // 5. Split the final HTML string by bracket placeholders
    const parts = finalHtml.split(/(\[.*?\])/g); // Capture the delimiters too

    // Track blank/dropdown index separately
    let blankCounter = 0;

    return parts.map((part, idx) => {
      if (part.startsWith("[") && part.endsWith("]")) {
        // This is a bracketed placeholder, determine if it's an Input or ReactSelect
        if (data?.type === "multiple_blanks") {
          const currentBlankIndex = blankCounter;
          blankCounter++;
          return (
            <Input
              key={idx}
              type="text"
              value={userAnswer[currentBlankIndex] || ""}
              disabled={isAttempted}
              onChange={(e) => {
                const val = e.target.value.split(" ");
                // Only validate word count if it's a positive number
                if (data?.wordsCount && data.wordsCount > 0 && val?.length > data?.wordsCount) {
                  return;
                }
                const a = [...userAnswer];
                a[currentBlankIndex] = e.target.value;
                updateUserAnswer(a);
              }}
              className={`custom-input ${
                isAttempted && !isTestAttempt && isCorrectAnswert
                  ? isCorrectAnswert(userAnswer)
                    ? "success-border"
                    : "error-border"
                  : ""
              }`}
            />
          );
        } else if (data?.type === "multiple_dropdown") {
          const currentDropdownIndex = blankCounter;
          blankCounter++;
          const opts = data.options?.[currentDropdownIndex]?.options || [];
          return (
            <ReactSelect
              key={idx}
              options={opts.map((o) => ({ label: o.option, value: o.option }))}
              isDisabled={isAttempted}
              value={
                userAnswer[currentDropdownIndex]
                  ? { label: userAnswer[currentDropdownIndex], value: userAnswer[currentDropdownIndex] }
                  : null
              }
              onChange={({ value }) => {
                const a = [...userAnswer];
                a[currentDropdownIndex] = value;
                updateUserAnswer(a);
              }}
              className={`custom-select ${
                isAttempted && !isTestAttempt && isCorrectAnswert
                  ? isCorrectAnswert(userAnswer)
                    ? "success-border"
                    : "error-border"
                  : ""
              }`}
              styles={{
                container: (p) => ({
                  ...p,
                  width: 180,
                  display: "inline-block",
                }),
                control: (p) => ({ ...p, height: 40, minHeight: 40 }),
                valueContainer: (p) => ({ ...p, padding: "0 8px" }),
                singleValue: (p) => ({ ...p, lineHeight: "56px" }),
              }}
            />
          );
        }
        return null;
      } else {
        // Plain HTML/Text (may include <span.latex-inline> and other tags)
        return <span key={idx} dangerouslySetInnerHTML={{ __html: part }} />;
      }
    });
  }, [
    content,
    data,
    userAnswer,
    updateUserAnswer,
    isAttempted,
    isTestAttempt,
    isCorrectAnswert,
  ]);

  // Effect to render KaTeX into the spans
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    // Iterate over all elements with the 'latex-inline' class and render KaTeX
    root.querySelectorAll(".latex-inline").forEach((el) => {
      const tex = he.decode(decodeURIComponent(el.getAttribute("data-latex")));
      try {
        katex.render(tex, el, {
          throwOnError: false, // Set to true for debugging KaTeX errors
          displayMode: false, // For inline math
        });
      } catch (err) {
        console.error("KaTeX render error for:", tex, err);
      }
    });
  }, [segments]); // Re-run when segments (which contain our HTML structure) change

  const isMultiline = content?.includes("\n") || content?.includes("<br>");
  const cls = isMultiline ? "statement-multiline" : "statement-container";

  return (
    <div ref={containerRef} className={cls + " no-hidden-overflow"}>
      {segments}
    </div>
  );
};

export default RenderLatexContent;
